// /om — about, and the only place the contact address is offered as an
// action. Was /hjalp until the 2026-09 rename; the file keeps its old name
// to limit churn, but the exported component is RapportvaktAboutPage.
//
// Punktlista is one person, so the page says so, and says it in the middle of
// one narrow column: a face, a name, a mailto button. Under it five questions a
// reader actually asks before they trust the product, each one collapsed until
// it is wanted, so the page opens as a person rather than as a wall of text.
//
// The questions are native <details>: they work with JavaScript off, which
// matters because this page is prerendered and indexed. Everything is static
// text from RV_STRINGS, so the HTML a crawler sees is the page a reader gets,
// and the answers stay bilingual.
//
// Nothing here predicts a date. "When does my first delivery arrive" is
// answered with the cadence rule, not with a guess.

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import RapportvaktShell, { useRvLang } from '../components/rapportvakt/Shell';
import { Glyph } from '../components/rapportvakt/Delivery';
import {
  RAPPORTVAKT_FOUNDER_PHOTO,
  contactEmail,
  rapportvaktTabPath,
} from '../config/rapportvakt';
import { getRapportvaktHelpSeo } from '../config/seoRoutes';

function HelpBody() {
  const { t } = useRvLang();
  const { hash } = useLocation();

  // A footer link ("Vanliga frågor" / "Common questions") points at
  // /om/#faq; a hard load lands there on its own via the browser's normal
  // hash handling, but a client-side navigation from elsewhere in the app
  // does not re-run that, so this does it by hand once the page is up.
  React.useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    document.getElementById(id)?.scrollIntoView();
  }, [hash]);

  return (
    <main className="rv__inner">
      <div className="rv-help">
        <h1 className="rv-help__title">{t.helpTitle}</h1>

        <section className="rv-help__intro">
          {/* A face, round, at the top of the card. The file is 600x800, so the
              box is given both dimensions and the image crops into it; the crop
              is pulled up because the face sits high in the frame. */}
          <img
            className="rv-help__photo"
            src={RAPPORTVAKT_FOUNDER_PHOTO}
            alt={t.helpPhotoAlt}
            width="600"
            height="800"
          />
          <p className="rv-help__greeting">{t.helpGreeting}</p>
          <p className="rv-help__lead">{t.helpIntro}</p>
          <p className="rv-help__sign">{t.helpSign}</p>
          {/* A button, not an <a href="mailto:...">: the address is joined
              only inside the click handler, so it never appears as text in
              this prerendered page's markup — see contactEmail() in
              src/config/rapportvakt.js for why. */}
          <button
            type="button"
            className="rv-btn rv-btn--primary"
            onClick={() => {
              window.location.href = `mailto:${contactEmail()}`;
            }}
          >
            {t.helpMailCta}
            <Glyph name="arrow" size={13} />
          </button>
        </section>

        <section className="rv-help__faq" aria-labelledby="faq">
          <h2 className="rv-help__head2" id="faq">
            {t.helpFaqHead}
          </h2>
          <div className="rv-faq">
            {t.helpFaq.map((item) => (
              <details className="rv-faq__item" key={item.q}>
                <summary className="rv-faq__q">
                  <span className="rv-faq__qtext">{item.q}</span>
                  <span className="rv-faq__mark" aria-hidden="true">
                    <Glyph name="chevron" size={15} />
                  </span>
                </summary>
                <p className="rv-faq__a">{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        <div className="rv-cta">
          <div>
            <h2 className="rv-cta__title">{t.closeTitle}</h2>
            <p className="rv-cta__sub">{t.priceNote}</p>
          </div>
          <Link className="rv-btn rv-btn--primary" to={rapportvaktTabPath('bevakningar')}>
            {t.heroCta}
            <Glyph name="arrow" size={13} />
          </Link>
        </div>
      </div>
    </main>
  );
}

export default function RapportvaktAboutPage() {
  return (
    <RapportvaktShell seo={getRapportvaktHelpSeo()}>
      <HelpBody />
    </RapportvaktShell>
  );
}
